import { Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { lazyWithRetry } from './utils/lazyWithRetry'
import { RouteLoading } from './components/RouteLoading'
import {
    RootRoute,
    RouteError,
    AuthenticatedRoute,
    RouteUrl,
    UnauthenticatedRoute,
    AuthenticatingRoute,
} from './routes'

// Lazy load route components with retry logic for failed chunk loads
const HomeRoute = lazyWithRetry(() =>
    import('./routes/home').then((m) => ({ default: m.HomeRoute }))
)
const LogInRoute = lazyWithRetry(() =>
    import('./routes/login').then((m) => ({ default: m.LogInRoute }))
)
const SignUpRoute = lazyWithRetry(() =>
    import('./routes/signup').then((m) => ({ default: m.SignUpRoute }))
)
const UnauthorizedRoute = lazyWithRetry(() =>
    import('./routes/unauthorized').then((m) => ({
        default: m.UnauthorizedRoute,
    }))
)
const PrivacyStatementRoute = lazyWithRetry(() =>
    import('./routes/privacy').then((m) => ({
        default: m.PrivacyStatementRoute,
    }))
)
const TermsAndConditionsRoute = lazyWithRetry(() =>
    import('./routes/termsAndConditions').then((m) => ({
        default: m.TermsAndConditionsRoute,
    }))
)

// New Consult routes
const NewConsultMainRoute = lazyWithRetry(() =>
    import('./routes/newConsult').then((m) => ({
        default: m.NewConsultMainRoute,
    }))
)
const NewConsultTranscriptionRoute = lazyWithRetry(() =>
    import('./routes/newConsult/transcription').then((m) => ({
        default: m.NewConsultTranscriptionRoute,
    }))
)

// History routes
const ConsultationsHistoryRoute = lazyWithRetry(() =>
    import('./routes/history').then((m) => ({
        default: m.ConsultationsHistoryRoute,
    }))
)
const ConsultDetailsByIdRoute = lazyWithRetry(() =>
    import('./routes/history/details').then((m) => ({
        default: m.ConsultDetailsByIdRoute,
    }))
)
const SharedWithMeRoute = lazyWithRetry(() =>
    import('./routes/history/shared').then((m) => ({
        default: m.SharedWithMeRoute,
    }))
)
const SharedConsultRoute = lazyWithRetry(() =>
    import('./routes/history/shared').then((m) => ({
        default: m.SharedConsultRoute,
    }))
)

// Patient routes
const PatientSearchRoute = lazyWithRetry(() =>
    import('./routes/patientSearch').then((m) => ({
        default: m.PatientSearchRoute,
    }))
)
const PatientDetailsRoute = lazyWithRetry(() =>
    import('./routes/patientSearch/details').then((m) => ({
        default: m.PatientDetailsRoute,
    }))
)
const PatientTranscriptionRoute = lazyWithRetry(() =>
    import('./routes/patientSearch/transcription').then((m) => ({
        default: m.PatientTranscriptionRoute,
    }))
)

// Settings routes
const SettingsRoute = lazyWithRetry(() =>
    import('./routes/settings').then((m) => ({ default: m.SettingsRoute }))
)
const ProfileRoute = lazyWithRetry(() =>
    import('./routes/settings/profile').then((m) => ({
        default: m.ProfileRoute,
    }))
)
const PlanManagementRoute = lazyWithRetry(() =>
    import('./routes/settings/planManagement').then((m) => ({
        default: m.PlanManagementRoute,
    }))
)
const PricingRoute = lazyWithRetry(() =>
    import('./routes/settings/pricing').then((m) => ({
        default: m.PricingRoute,
    }))
)
const LanguageRoute = lazyWithRetry(() =>
    import('./routes/settings/language').then((m) => ({
        default: m.LanguageRoute,
    }))
)
const ReportRoute = lazyWithRetry(() =>
    import('./routes/settings/report').then((m) => ({
        default: m.ReportRoute,
    }))
)
const SettingsTermsAndConditionsRoute = lazyWithRetry(() =>
    import('./routes/settings/termsAndConditions').then((m) => ({
        default: m.TermsAndConditionsRoute,
    }))
)
const SettingsPrivacyRoute = lazyWithRetry(() =>
    import('./routes/settings/privacy').then((m) => ({
        default: m.PrivacyRoute,
    }))
)
const LegalRoute = lazyWithRetry(() =>
    import('./routes/settings/legal').then((m) => ({ default: m.LegalRoute }))
)
const CheckoutSuccessRoute = lazyWithRetry(() =>
    import('./routes/settings/checkoutSuccess').then((m) => ({
        default: m.CheckoutSuccessRoute,
    }))
)

// Forms routes — imported directly (not lazy) to avoid React production bug
// where navigating between lazy form routes triggers "Cannot assign to read
// only property '_status'" due to Object.freeze on lazy wrapper objects.
import { FormsRoute } from './routes/forms'
import { FormReviewRoute } from './routes/forms/review'
import { FormEditRoute } from './routes/forms/edit'

// Admin routes — guard imported directly (thin access-control wrapper),
// landing lazy-loaded to keep the admin bundle out of the main chunk.
import { AdminRoute } from './routes/admin/AdminRoute'
import { AdminShell } from './features/admin/AdminShell'
const AdminTemplatesRoute = lazyWithRetry(() =>
    import('./routes/admin/templates').then((m) => ({
        default: m.AdminTemplatesRoute,
    }))
)

const AdminFormsRoute = lazyWithRetry(() =>
    import('./routes/admin/forms').then((m) => ({ default: m.AdminFormsRoute }))
)

const AdminUsersRoute = lazyWithRetry(() =>
    import('./routes/admin/users').then((m) => ({ default: m.AdminUsersRoute }))
)

const AdminLogsRoute = lazyWithRetry(() =>
    import('./routes/admin/logs').then((m) => ({ default: m.AdminLogsRoute }))
)

export const routes = [
    {
        path: RouteUrl.Root.segment,
        element: <RootRoute />,
        errorElement: <RouteError />,
        children: [
            {
                path: RouteUrl.Home.segment,
                element: <UnauthenticatedRoute />,
                children: [
                    {
                        index: true,
                        element: (
                            <Suspense fallback={<RouteLoading />}>
                                <HomeRoute />
                            </Suspense>
                        ),
                    },
                    {
                        path: RouteUrl.SignUp.segment,
                        element: (
                            <Suspense fallback={<RouteLoading />}>
                                <SignUpRoute />
                            </Suspense>
                        ),
                    },
                    {
                        path: RouteUrl.LogIn.segment,
                        element: (
                            <Suspense fallback={<RouteLoading />}>
                                <LogInRoute />
                            </Suspense>
                        ),
                    },
                    {
                        path: RouteUrl.Unauthorized.segment,
                        element: (
                            <Suspense fallback={<RouteLoading />}>
                                <UnauthorizedRoute />
                            </Suspense>
                        ),
                    },
                    {
                        path: RouteUrl.PrivacyStatement.segment,
                        element: (
                            <Suspense fallback={<RouteLoading />}>
                                <PrivacyStatementRoute />
                            </Suspense>
                        ),
                    },
                    {
                        path: RouteUrl.TermsConditions.segment,
                        element: (
                            <Suspense fallback={<RouteLoading />}>
                                <TermsAndConditionsRoute />
                            </Suspense>
                        ),
                    },
                    {
                        // Legacy/short link: /terms redirects to the canonical
                        // /terms-and-conditions page (ACA-21).
                        path: 'terms',
                        element: (
                            <Navigate
                                to={RouteUrl.TermsConditions.path}
                                replace
                            />
                        ),
                    },
                ],
            },
            {
                path: RouteUrl.Authenticated.segment,
                element: <AuthenticatingRoute />,
            },
            {
                path: RouteUrl.NewConsult.segment,
                element: <AuthenticatedRoute />,
                children: [
                    {
                        index: true,
                        element: (
                            <Suspense fallback={<RouteLoading />}>
                                <NewConsultMainRoute />
                            </Suspense>
                        ),
                    },
                    {
                        path: RouteUrl.NewConsultTranscription.segment,
                        element: (
                            <Suspense fallback={<RouteLoading />}>
                                <NewConsultTranscriptionRoute />
                            </Suspense>
                        ),
                    },
                ],
            },
            {
                path: RouteUrl.History.segment,
                element: <AuthenticatedRoute />,
                children: [
                    {
                        index: true,
                        element: (
                            <Suspense fallback={<RouteLoading />}>
                                <ConsultationsHistoryRoute />
                            </Suspense>
                        ),
                    },
                    {
                        path: RouteUrl.HistoryConsultDetailsById.segment,
                        element: (
                            <Suspense fallback={<RouteLoading />}>
                                <ConsultDetailsByIdRoute isEditable={true} />
                            </Suspense>
                        ),
                    },
                    {
                        path: RouteUrl.HistoryShared.segment,
                        element: (
                            <Suspense fallback={<RouteLoading />}>
                                <SharedWithMeRoute />
                            </Suspense>
                        ),
                    },
                    {
                        path: RouteUrl.HistorySharedConsultById.segment,
                        element: (
                            <Suspense fallback={<RouteLoading />}>
                                <SharedConsultRoute />
                            </Suspense>
                        ),
                    },
                ],
            },
            {
                path: RouteUrl.Patient.segment,
                element: <AuthenticatedRoute />,
                children: [
                    {
                        index: true,
                        element: (
                            <Suspense fallback={<RouteLoading />}>
                                <PatientSearchRoute />
                            </Suspense>
                        ),
                    },
                    {
                        path: RouteUrl.PatientById.segment,
                        element: (
                            <Suspense fallback={<RouteLoading />}>
                                <PatientDetailsRoute />
                            </Suspense>
                        ),
                    },
                    {
                        path: RouteUrl.PatientConsultDetailsById.segment,
                        element: (
                            <Suspense fallback={<RouteLoading />}>
                                <ConsultDetailsByIdRoute isEditable={true} />
                            </Suspense>
                        ),
                    },
                    {
                        path: RouteUrl.PatientTranscriptionById.segment,
                        element: (
                            <Suspense fallback={<RouteLoading />}>
                                <PatientTranscriptionRoute />
                            </Suspense>
                        ),
                    },
                ],
            },
            {
                path: RouteUrl.Settings.segment,
                element: <AuthenticatedRoute />,
                children: [
                    {
                        index: true,
                        element: <Navigate to={RouteUrl.Profile.segment} />,
                    },
                    {
                        path: '*',
                        element: (
                            <Suspense fallback={<RouteLoading />}>
                                <SettingsRoute />
                            </Suspense>
                        ),
                        children: [
                            {
                                path: RouteUrl.Profile.segment,
                                element: (
                                    <Suspense fallback={<RouteLoading />}>
                                        <ProfileRoute />
                                    </Suspense>
                                ),
                            },
                            {
                                path: RouteUrl.PlanManagement.segment,
                                element: (
                                    <Suspense fallback={<RouteLoading />}>
                                        <PlanManagementRoute />
                                    </Suspense>
                                ),
                            },
                            {
                                path: RouteUrl.Language.segment,
                                element: (
                                    <Suspense fallback={<RouteLoading />}>
                                        <LanguageRoute />
                                    </Suspense>
                                ),
                            },
                            {
                                path: RouteUrl.ReportIssue.segment,
                                element: (
                                    <Suspense fallback={<RouteLoading />}>
                                        <ReportRoute />
                                    </Suspense>
                                ),
                            },
                            {
                                path: RouteUrl.SettingsTermsAndConditions
                                    .segment,
                                element: (
                                    <Suspense fallback={<RouteLoading />}>
                                        <SettingsTermsAndConditionsRoute />
                                    </Suspense>
                                ),
                            },
                            {
                                path: RouteUrl.Privacy.segment,
                                element: (
                                    <Suspense fallback={<RouteLoading />}>
                                        <SettingsPrivacyRoute />
                                    </Suspense>
                                ),
                            },
                            {
                                path: RouteUrl.Legal.segment,
                                element: (
                                    <Suspense fallback={<RouteLoading />}>
                                        <LegalRoute />
                                    </Suspense>
                                ),
                            },
                        ],
                    },
                    {
                        path: RouteUrl.PlanPricing.segment,
                        element: (
                            <Suspense fallback={<RouteLoading />}>
                                <PricingRoute />
                            </Suspense>
                        ),
                    },
                ],
            },
            {
                path: RouteUrl.Forms.segment,
                element: <AuthenticatedRoute />,
                children: [
                    {
                        index: true,
                        element: <FormsRoute />,
                    },
                    {
                        path: RouteUrl.FormReviewById.segment,
                        element: <FormReviewRoute />,
                    },
                    {
                        path: RouteUrl.FormEditById.segment,
                        element: <FormEditRoute />,
                    },
                ],
            },
            {
                path: RouteUrl.Admin.segment,
                element: <AuthenticatedRoute />,
                children: [
                    {
                        // Pathless layout route: gate the entire admin subtree
                        // behind the centralized admin check before rendering
                        // any admin page.
                        element: <AdminRoute />,
                        children: [
                            {
                                // ACA-145: single tabbed admin shell (header +
                                // Users/Templates/Forms tabs) wrapping every
                                // admin screen via the routed Outlet.
                                element: <AdminShell />,
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <Navigate
                                                to={RouteUrl.AdminUsers.path}
                                                replace
                                            />
                                        ),
                                    },
                                    {
                                        path: RouteUrl.AdminTemplates.segment,
                                        element: (
                                            <Suspense
                                                fallback={<RouteLoading />}
                                            >
                                                <AdminTemplatesRoute />
                                            </Suspense>
                                        ),
                                    },
                                    {
                                        path: RouteUrl.AdminForms.segment,
                                        element: (
                                            <Suspense
                                                fallback={<RouteLoading />}
                                            >
                                                <AdminFormsRoute />
                                            </Suspense>
                                        ),
                                    },
                                    {
                                        path: RouteUrl.AdminUsers.segment,
                                        element: (
                                            <Suspense
                                                fallback={<RouteLoading />}
                                            >
                                                <AdminUsersRoute />
                                            </Suspense>
                                        ),
                                    },
                                    {
                                        path: RouteUrl.AdminLogs.segment,
                                        element: (
                                            <Suspense
                                                fallback={<RouteLoading />}
                                            >
                                                <AdminLogsRoute />
                                            </Suspense>
                                        ),
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                path: RouteUrl.CheckoutSuccess.segment,
                element: <AuthenticatedRoute />,
                children: [
                    {
                        index: true,
                        element: (
                            <Suspense fallback={<RouteLoading />}>
                                <CheckoutSuccessRoute />
                            </Suspense>
                        ),
                    },
                ],
            },
        ],
    },
]

export const router = createBrowserRouter(routes)
